import MediaRecorderEncoder from 'opus-media-recorder';
import * as Sentry from '@sentry/browser';
import { toast } from 'react-toastify';

// eslint-disable-next-line import/no-webpack-loader-syntax,import/no-unresolved,import/extensions
import EncoderWorker from 'worker-loader!opus-media-recorder/encoderWorker.js';

import { MediaRecorder, MediaRecorderState } from '../../../../chat';
import {
  BrowserMediaRecorder,
  getNativeRecorderMimeType,
  createNativeRecorderSetup,
  getOpusWorkerOptions,
  OPUS_WAV_MIME_TYPE,
} from '../util/voiceRecordingUtils';

import texts from './texts.json';

class WebMediaRecorder extends MediaRecorder {
  async checkForPermissions() {
    if (this.permissionsGranted) {
      return true;
    }

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.stream = audioStream;
      this.permissionsGranted = !!audioStream;
      return this.permissionsGranted;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          description: texts.errorMessage.permissions,
        },
      });
      return false;
    }
  }

  async startRecording(onDataAvailable) {
    if (!(await this.checkForPermissions())) {
      return;
    }
    await super.startRecording();

    const nativeMimeType = getNativeRecorderMimeType();
    if (nativeMimeType && BrowserMediaRecorder) {
      try {
        this._startNativeRecording(onDataAvailable, nativeMimeType);
        return;
      } catch (e) {
        this.recorder = null;
        Sentry.captureException(e, {
          extra: { context: 'Native MediaRecorder failed, falling back to opus-media-recorder' },
        });
      }
    }
    this._startOpusRecording(onDataAvailable);
  }

  _startNativeRecording(onDataAvailable, mimeType) {
    this.recorder = createNativeRecorderSetup(this.stream, mimeType, BrowserMediaRecorder, {
      onStop: (blob) => onDataAvailable({ data: blob, mimeType: blob.type || mimeType }),
      onError: (err) => {
        this.state = MediaRecorderState.ERROR;
        toast.error(texts.errorMessage.default);
        this.stream.getTracks().forEach((track) => track.stop());
        Sentry.captureException(err, { extra: { description: texts.errorMessage.webMediaRecording } });
      },
    });
  }

  _startOpusRecording(onDataAvailable) {
    const encoderWorkerFactory = () => new EncoderWorker();
    const workerOptions = getOpusWorkerOptions(encoderWorkerFactory);
    const mimeType = OPUS_WAV_MIME_TYPE;
    const recorder = new MediaRecorderEncoder(this.stream, { mimeType }, workerOptions);
    const chunks = [];

    recorder.addEventListener('dataavailable', (audioData) => {
      if (audioData.data && audioData.data.size > 0) chunks.push(audioData.data);
    });
    recorder.addEventListener('stop', () => {
      if (chunks.length === 0) return;
      const finalBlob = new Blob(chunks, { type: mimeType });
      if (finalBlob.size > 0) {
        onDataAvailable({ data: finalBlob, mimeType: finalBlob.type || mimeType });
      }
    });
    recorder.addEventListener('error', (error) => {
      this.state = MediaRecorderState.ERROR;
      toast.error(texts.errorMessage.default);
      this.stream.getTracks().forEach((track) => track.stop());
      Sentry.captureException(error, {
        extra: {
          description: texts.errorMessage.webMediaRecording,
        },
      });
    });
    recorder.start();
    this.recorder = recorder;
  }

  stopRecording() {
    if (!this.recorder) return;
    this.recorder.stop();
    this.state = MediaRecorderState.INACTIVE;
  }
}

export default WebMediaRecorder;
